import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { assets } from "../../../assets/assets";
import { CreateContractModal } from "../../../pages/contracts/create/CreateContractModal";
import { CreateProjectModal } from "../../../pages/projects/CreateProjectModal";
import { useCreatePublicSourcingEventModal } from "../../../pages/sourcing-events/buyer/public/useCreatePublicSourcingEventModal";
import { urls } from "../../../urls";
import { useCreateNeed } from "../../../pages/v2/behov/useCreateNeed";
import { AssistantPanelTrigger } from "../../agent/AssistantPanelTrigger";
import { useSub } from "../../subscription/useSub";
import { NotificationBell } from "../../notifications/v2/NotificationBell";
import type { AppRole, NavSectionAction, SectionActionKind } from "./navConfig";
import { navConfig } from "./navConfig";
import { SidebarSection } from "./SidebarSection";
import { SidebarUserMenu } from "./SidebarUserMenu";
import type { NavBadges } from "./useNavBadges";

const SIDEBAR_WIDTH = "260px";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: AppRole;
  badges: NavBadges;
}

const SidebarContent = ({
  role,
  badges,
  onNavigate,
  onAction,
}: {
  role: AppRole;
  badges: NavBadges;
  onNavigate: (path: string, query?: string) => void;
  onAction: (action: NavSectionAction) => void;
}) => {
  const { colorMode } = useColorMode();
  const sub = useSub();
  const sections = navConfig.filter((section) => !section.access || sub[section.access]);
  return (
    <Flex direction="column" h="full">
      <Flex
        align="center"
        justify="space-between"
        h="60px"
        px="4"
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="smBorder"
      >
        <RouterLink to={urls.v2.home}>
          <Image src={colorMode === "dark" ? assets.logo_dark : assets.logo} alt="SourceMagnet" h="26px" />
        </RouterLink>
        {/* Desktop only: on mobile the (drawer) sidebar is paired with the top bar, which carries the bell. */}
        <Flex display={{ base: "none", lg: "flex" }} align="center">
          <AssistantPanelTrigger />
          <NotificationBell placement="bottom-start" />
        </Flex>
      </Flex>
      <VStack as="nav" flex="1" align="stretch" spacing="0" px="3" py="2" overflowY="auto">
        {sections.map((section) => (
          <SidebarSection
            key={section.titleKey}
            section={section}
            role={role}
            badges={badges}
            onNavigate={onNavigate}
            onAction={onAction}
          />
        ))}
      </VStack>
      <SidebarUserMenu />
    </Flex>
  );
};

export const Sidebar = ({ isOpen, onClose, role, badges }: Props) => {
  const navigate = useNavigate();
  const { create: createNeed } = useCreateNeed();
  const { show: showCreatePublicModal, modal: createPublicModal } = useCreatePublicSourcingEventModal();
  const [showCreateContractModal, setShowCreateContractModal] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  const onNavigate = (path: string, query?: string) => {
    if (path === urls.v2.needs.new) {
      void createNeed();
      onClose();
      return;
    }
    navigate(query ? `${path}?${query}` : path);
    onClose();
  };

  const actionHandlers: Record<SectionActionKind, () => void> = {
    createSourcingEvent: showCreatePublicModal,
    createContract: () => setShowCreateContractModal(true),
    createProject: () => setShowCreateProjectModal(true),
  };

  const onAction = (action: NavSectionAction) => {
    actionHandlers[action.kind]();
    onClose();
  };

  return (
    <>
      <Box
        as="aside"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        w={SIDEBAR_WIDTH}
        flexShrink={0}
        h="full"
        overflow="hidden"
        bg="smBackground"
        borderRight="1px solid"
        borderColor="smBorder"
      >
        <SidebarContent role={role} badges={badges} onNavigate={onNavigate} onAction={onAction} />
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerBody p="0">
            <SidebarContent role={role} badges={badges} onNavigate={onNavigate} onAction={onAction} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {createPublicModal}
      {showCreateContractModal && <CreateContractModal onClose={() => setShowCreateContractModal(false)} />}
      {showCreateProjectModal && <CreateProjectModal isOpen setShowModal={setShowCreateProjectModal} />}
    </>
  );
};
